/**
 * Trip File Manager
 * Auto-saves trips to user-chosen files via File System Access API.
 * File handles are persisted in IndexedDB so the user only picks
 * the file location once — on subsequent loads the browser asks for
 * a simple "Allow" tap (no full file picker needed).
 *
 * Supported: Chrome / Edge (desktop + Android).
 * On unsupported browsers (iOS Safari, Firefox) OPFS takes over.
 */

import dataManager from './DataManager';
import storageManager from './storageManager';

class TripFileManager {
  constructor() {
    this.fileHandles      = new Map(); // tripId -> FileSystemFileHandle
    this.savingStatus     = new Map(); // tripId -> 'saving' | 'saved' | 'error'
    this.lastSaved        = new Map(); // tripId -> timestamp
    this.autoSaveEnabled  = new Map(); // tripId -> boolean
    this._pendingPerm     = new Set(); // tripIds waiting for requestPermission gesture
    this._restored        = false;
  }

  // ── Browser support ────────────────────────────────────────────────────────

  isSupported() {
    return 'showSaveFilePicker' in window;
  }

  getSupportInfo() {
    const supported = this.isSupported();
    const ua = navigator.userAgent;
    let browser = 'tu navegador';
    if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    return {
      isSupported: supported,
      browser,
      message: supported
        ? 'Auto-guardado en archivo disponible'
        : `${browser} no soporta guardado en archivo. Tus datos están protegidos por OPFS.`,
    };
  }

  // ── Startup: restore persisted file handles ────────────────────────────────

  async restoreFileHandles() {
    if (this._restored || !this.isSupported()) return;
    this._restored = true;
    try {
      await storageManager.initialize();
      const all = await storageManager.getAllFileHandles();
      for (const [tripId, handle] of Object.entries(all)) {
        if (!handle?.queryPermission) continue;
        const perm = await handle.queryPermission({ mode: 'readwrite' });
        if (perm === 'granted') {
          this.fileHandles.set(tripId, handle);
          this.autoSaveEnabled.set(tripId, true);
        } else if (perm === 'prompt') {
          this.fileHandles.set(tripId, handle);
          this.autoSaveEnabled.set(tripId, false);
          this._pendingPerm.add(tripId);
        }
      }
      if (this._pendingPerm.size > 0) {
        window.dispatchEvent(new CustomEvent('wayfare-fsa-permission-needed', {
          detail: { tripIds: [...this._pendingPerm] },
        }));
      }
    } catch (e) {
      console.warn('[FSA] restoreFileHandles failed:', e.message);
    }
  }

  // Call from a button click (requires user gesture) to re-grant permission
  async requestPendingPermissions() {
    if (this._pendingPerm.size === 0) return 0;
    let granted = 0;
    for (const tripId of [...this._pendingPerm]) {
      const handle = this.fileHandles.get(tripId);
      if (!handle) continue;
      try {
        const perm = await handle.requestPermission({ mode: 'readwrite' });
        if (perm === 'granted') {
          this.autoSaveEnabled.set(tripId, true);
          this._pendingPerm.delete(tripId);
          granted++;
          this.autoSave(tripId).catch(() => {});
        }
      } catch (_) {}
    }
    return granted;
  }

  hasPendingPermissions() {
    return this._pendingPerm.size > 0;
  }

  // ── Setup (first time) ─────────────────────────────────────────────────────

  async setupAutoSave(tripId, tripData) {
    if (!this.isSupported()) {
      return {
        success: false, reason: 'not_supported',
        message: 'Tu navegador no soporta guardado en archivo. Usa Chrome.',
      };
    }
    try {
      const city = (tripData.destination?.city || 'Trip')
        .replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
      const date = new Date().toISOString().split('T')[0];
      const name = `${city}-${date}.trip`;

      const handle = await window.showSaveFilePicker({
        suggestedName: name,
        types: [{ description: 'Wayfare Trip File', accept: { 'application/json': ['.trip'] } }],
      });

      this.fileHandles.set(tripId, handle);
      this.autoSaveEnabled.set(tripId, true);

      // Persist handle so it survives page reloads
      await storageManager.saveFileHandle(tripId, handle).catch(() => {});

      await this._writeToFile(handle, tripId, tripData);

      return { success: true, fileName: handle.name,
        message: `Auto-guardado activado: ${handle.name}` };
    } catch (e) {
      if (e.name === 'AbortError') {
        return { success: false, reason: 'user_cancelled', message: 'Cancelado' };
      }
      return { success: false, reason: 'error', message: e.message };
    }
  }

  // ── Auto-save ──────────────────────────────────────────────────────────────

  async autoSave(tripId) {
    if (!this.autoSaveEnabled.get(tripId)) {
      return { success: false, reason: 'not_enabled', skipped: true };
    }
    const handle = this.fileHandles.get(tripId);
    if (!handle) return { success: false, reason: 'no_handle' };

    try {
      this.savingStatus.set(tripId, 'saving');
      this._emit(tripId, 'saving');

      const tripData = await dataManager.getTrip(tripId);
      if (!tripData) throw new Error('Trip not found');
      await this._writeToFile(handle, tripId, tripData);

      this.savingStatus.set(tripId, 'saved');
      this.lastSaved.set(tripId, Date.now());
      this._emit(tripId, 'saved');
      return { success: true, savedAt: new Date().toISOString() };
    } catch (e) {
      this.savingStatus.set(tripId, 'error');
      this._emit(tripId, 'error');
      return { success: false, message: e.message };
    }
  }

  // ── File write ─────────────────────────────────────────────────────────────

  async _writeToFile(handle, tripId, tripData) {
    const photos = await dataManager.getPhotos(tripId);
    const budget = await dataManager.getBudget(tripId);

    const tripFile = {
      version: '2.2',
      format: 'wayfare-trip',
      tripId: tripData.tripId,
      destination: tripData.destination,
      dates: tripData.dates,
      status: tripData.status,
      lastUpdated: new Date().toISOString(),
      autoBackupEnabled: true,
      stats: tripData.stats,
      data: {
        notes:            tripData.notes            || [],
        reflections:      tripData.reflections      || [],
        challenges:       tripData.challenges       || [],
        luggage:          tripData.luggage          || [],
        groceries:        tripData.groceries        || [],
        gifts:            tripData.gifts            || [],
        lodgings:         tripData.lodgings         || [],
        privateDocuments: tripData.privateDocuments || [],
        travelInfo:       tripData.travelInfo       || {},
        // Sprint 1 — proximity reveal / serendipity (per-trip, additive)
        revealedPois:     tripData.revealedPois     || [],
        surfacedPois:     tripData.surfacedPois     || [],
        dayClosures:      tripData.dayClosures      || {},
        // Sprint 2 — sealed book + open threads (additive)
        sealedAt:         tripData.sealedAt         || null,
        openThreads:      tripData.openThreads      || [],
        budget,
        photos: await this._exportPhotos(photos),
      },
      createdAt:    tripData.createdAt,
      lastModified: tripData.lastModified,
    };

    const w = await handle.createWritable();
    await w.write(JSON.stringify(tripFile, null, 2));
    await w.close();
  }

  async _exportPhotos(photos) {
    const out = [];
    for (const p of photos) {
      try {
        out.push({
          id: p.id, tripId: p.tripId,
          data: await this._blobToBase64(p.data),
          caption: p.caption || '', location: p.location || '',
          timestamp: p.timestamp, size: p.size || 0,
        });
      } catch (_) {}
    }
    return out;
  }

  _blobToBase64(blob) {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onloadend = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  }

  _base64ToBlob(b64) {
    return fetch(b64).then(r => r.blob());
  }

  // ── Status ─────────────────────────────────────────────────────────────────

  _emit(tripId, status) {
    window.dispatchEvent(new CustomEvent('trip-autosave-status', {
      detail: { tripId, status, lastSaved: this.lastSaved.get(tripId) },
    }));
  }

  getStatus(tripId) {
    return {
      enabled:         this.autoSaveEnabled.get(tripId) || false,
      status:          this.savingStatus.get(tripId)    || 'idle',
      lastSaved:       this.lastSaved.get(tripId),
      hasFileHandle:   this.fileHandles.has(tripId),
      needsPermission: this._pendingPerm.has(tripId),
    };
  }

  disableAutoSave(tripId) {
    this.autoSaveEnabled.set(tripId, false);
    this.fileHandles.delete(tripId);
    this.savingStatus.delete(tripId);
    storageManager.deleteFileHandle(tripId).catch(() => {});
  }

  clearAll() {
    this.fileHandles.clear();
    this.savingStatus.clear();
    this.lastSaved.clear();
    this.autoSaveEnabled.clear();
    this._pendingPerm.clear();
  }

  // ── Import / Restore ───────────────────────────────────────────────────────

  async importTripFile(file) {
    try {
      const tripFile = JSON.parse(await file.text());
      if (tripFile.format !== 'wayfare-trip' || !tripFile.tripId || !tripFile.data) {
        throw new Error('Invalid .trip file format');
      }
      return { success: true, tripData: tripFile,
        message: `Trip imported: ${tripFile.destination?.city || 'Unknown'}` };
    } catch (e) {
      return { success: false, reason: 'invalid_file', message: e.message };
    }
  }

  async restoreTripFromFile(tripFile) {
    try {
      const existing = await dataManager.getTrip(tripFile.tripId);
      const payload  = {
        destination:      tripFile.destination,
        dates:            tripFile.dates,
        status:           tripFile.status,
        notes:            tripFile.data.notes,
        reflections:      tripFile.data.reflections,
        challenges:       tripFile.data.challenges,
        luggage:          tripFile.data.luggage,
        groceries:        tripFile.data.groceries,
        gifts:            tripFile.data.gifts,
        lodgings:         tripFile.data.lodgings,
        privateDocuments: tripFile.data.privateDocuments,
        travelInfo:       tripFile.data.travelInfo,
        revealedPois:     tripFile.data.revealedPois || [],
        surfacedPois:     tripFile.data.surfacedPois || [],
        dayClosures:      tripFile.data.dayClosures  || {},
        sealedAt:         tripFile.data.sealedAt     || null,
        openThreads:      tripFile.data.openThreads  || [],
      };

      if (existing) {
        await dataManager.updateTrip(tripFile.tripId, payload);
      } else {
        await dataManager.createTrip({ tripId: tripFile.tripId, ...payload }, false);
      }

      if (tripFile.data.photos?.length) {
        for (const p of tripFile.data.photos) {
          try {
            await dataManager.addPhoto(tripFile.tripId, {
              data: await this._base64ToBlob(p.data),
              caption: p.caption, location: p.location,
            });
          } catch (_) {}
        }
      }

      if (tripFile.data.budget) {
        await dataManager.saveBudget(tripFile.tripId, tripFile.data.budget);
      }

      return { success: true, tripId: tripFile.tripId,
        message: `Trip restored: ${tripFile.destination?.city || 'Unknown'}` };
    } catch (e) {
      return { success: false, message: e.message };
    }
  }
}

const tripFileManager = new TripFileManager();
export default tripFileManager;
